import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { typeInfo } from "../../../../../constants/typeInfo";
import { applyUnderline } from "../../../../../utils/textUtils";

import CommonPage from "../../common/CommonPage";

const BasicChoicePage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();

  const [overlayVisible, setOverlayVisible] = useState(false);
  // [해설 접이식] 제출 직후엔 펼침이 기본, 헤더 탭으로 접기 가능.
  // 다음 문항으로 넘어가면 다시 펼침 상태로 리셋.
  const [explOpen, setExplOpen] = useState(true);
  useEffect(() => {
    setExplOpen(true);
  }, [currentIndex]);

  // [문제뱅크 듣기] 음원이 있는 문항의 재생. 플레이어와 같은 규칙으로 문항당
  // 최대 2번만 들려준다. 문항을 넘기면 재생을 멈춘다.
  const [audioPlayCounts, setAudioPlayCounts] = useState({});
  const audioRef = useRef(null);
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, [currentIndex]);
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);
  const audioPlayedCount = audioPlayCounts[currentIndex] || 0;
  // 받아쓰기(2차)는 3번, 1차 객관식은 2번까지 (예전 플레이어와 같은 값)
  // 받아쓰기는 듣는 횟수를 막지 않는다. 세 번 듣고 받아쓰라는 것은 듣기 시험이지
  // 받아쓰기 연습이 아니다. 정식 듣기 문항만 재생 2번으로 둔다.
  const audioPlayLimit = currentPage.type === "bank_dictation" ? Infinity : 2;
  const playQuestionAudio = () => {
    if (audioPlayedCount >= audioPlayLimit || !currentPage.audio) return;
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = new Audio(currentPage.audio);
    audioRef.current.play().catch(() => {});
    setAudioPlayCounts({ ...audioPlayCounts, [currentIndex]: audioPlayedCount + 1 });
  };

  // [어법 개선안 반영] 콘텐츠 640px 중앙 열 + Q칩 + 우상단 카운터 + 진행바.
  // 근거: 기존 75vw 전폭 보기 바는 문항(좌)→보기(중앙) 시선 왕복이 크고,
  // 진행 표시(1/100)가 화면 최하단(CommonPage 고정바)에만 있어 잘려 보였음.
  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768;
  const accent = typeInfo["grammar"]?.color || "#E8920A"; // 어법 앰버 토큰

  // [문제뱅크 주관식] 서답형 입력. 답 비교는 공백·대소문자·끝 문장부호에
  // 관대하게 한다 (배열형 문장은 마침표 유무로 틀리면 억울하다).
  const [shortInput, setShortInput] = useState("");
  const [clozeInputs, setClozeInputs] = useState({});
  // 고쳐 쓰기 두 칸 {wrong, right}
  const [fixInputs, setFixInputs] = useState({ wrong: "", right: "" });

  // 선지가 달린 빈칸만 어법 복수 괄호로 그린다. 주관식 빈칸(bank_cloze)은
  // 선지가 없어서 같이 그리면 죽는다.
  const choiceBlankList = (currentPage.blanks || []).filter((blank) =>
    Array.isArray(blank.choices)
  );
  useEffect(() => {
    setShortInput(currentPage.input_text || "");
    setClozeInputs(currentPage.blank_inputs || {});
  }, [currentIndex]);

  // [문제뱅크 배열형] "우리말과 일치하도록 ... 배열" 문항은 타이핑 대신
  // 단어 칩을 눌러 문장을 조립한다. 문항에 안 쓰는 미끼 단어(whom/which 중
  // 하나 같은 것)가 섞여 있어, 다 쓰는 게 아니라 골라 쓰는 유형이다.
  // 발문 속 "w1 / w2 / ..." 줄을 칩으로 바꾸고 그 줄은 발문에서 감춘다.
  const arrangeWordList = (() => {
    if (currentPage.type !== "bank_short") return null;
    const lineList = String(currentPage.question || "").split("\n");
    if (!lineList[0] || !lineList[0].includes("배열")) return null;
    const slashedLine = lineList.find((line) => line.split(" / ").length >= 3);
    if (!slashedLine) return null;
    const words = slashedLine.split(" / ").map((word) => word.trim()).filter(Boolean);
    return { words, slashedLine };
  })();
  // 누른 순서대로 칩 번호를 쌓는다. 같은 단어가 두 번 나와도 번호라 안 겹친다.
  const [arrangePicks, setArrangePicks] = useState([]);
  useEffect(() => {
    setArrangePicks(currentPage.arrange_picks || []);
  }, [currentIndex]);
  const arrangeText = arrangeWordList
    ? arrangePicks.map((pickIndex) => arrangeWordList.words[pickIndex]).join(" ")
    : "";

  // [힌트] 틀리면 정답을 바로 보여 주지 않고 앞 글자부터 하나씩 열어 준다.
  // p → pl → pla ... 아이가 유추해서 스스로 적게 하려는 것이다.
  // {칸키: 열어 준 글자 수}
  const [hintSteps, setHintSteps] = useState({});
  useEffect(() => {
    setHintSteps(currentPage.hint_steps || {});
    setFixInputs({ wrong: "", right: "" });
  }, [currentIndex]);

  // [어법 복수 괄호] 한 문장에서 괄호마다 하나씩 고른다 (문장당 최대 3개).
  // {괄호번호(0부터): 고른 선지(1부터)}. 마지막 괄호를 고르는 순간 한꺼번에 채점한다.
  const [blankPicks, setBlankPicks] = useState({});
  // 빈칸 하나를 고른 직후의 O/X 오버레이. 문항 전체 정오와 별개다.
  const [overlayCorrect, setOverlayCorrect] = useState(null);
  useEffect(() => {
    setBlankPicks(currentPage.blank_picks || {});
    setOverlayCorrect(null);
  }, [currentIndex]);

  // **힌트는 답 쓰는 칸 안에 흐리게 놓는다.** 칸 밖에 따로 적으면 아이가 힌트를
  // 보면서 칸을 채우느라 눈이 왔다 갔다 한다(2026-09-05 사용자 지시).
  // 남은 글자는 밑줄로 자리를 보여 준다. "공 _ _ _" 처럼 몇 글자인지가 보인다.
  const hintPlaceholder = (answer, step) => {
    const text = String(answer || "");
    if (!step) return "";
    const opened = text.slice(0, step);
    const restList = [...text.slice(step)].map((letter) =>
      letter.trim() ? "_" : " "
    );
    return `${opened} ${restList.join(" ")}`.replace(/\s+$/, "");
  };

  // **답을 나누는 기호를 가리지 않는다.** 입력칸은 하나인데 답은 여러 조각인
  // 문항이 있다. 아이가 기호를 어떻게 넣어야 하는지 몰라 맞게 쓰고도 틀렸다.
  //
  // - 빈칸 둘: "climbing / trying" (2026-09-02 플러스업 손민아)
  // - 고쳐 쓰기: "choosing → to choose" 인데 아이는 `->` 를 친다. 화살표 문항
  //   30개 중 맞은 것이 4개뿐이었다(2026-09-04 지인학원 배유나·이윤서·오은채).
  //
  // 슬래시·쉼표·화살표·띄어쓰기를 모두 같은 것으로 보고 견준다. 끝에 붙은
  // 문장부호와 그 앞 빈칸도 털어 낸다("bike !" 도 맞다).
  const normalizeAnswer = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\u2192|\u21d2|->|=>/g, " / ")
      .replace(/[/,]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/[\s.!?。]+$/, "");

  // 고쳐 쓰기 문항은 **고친 쪽만 써도 맞다.** "choosing → to choose" 에서 아이가
  // "to choose" 만 적는 것은 문제가 시킨 일을 다 한 것이다. 틀린 쪽만 적은 것은
  // 고친 것이 아니므로 맞다고 하지 않는다.
  const acceptedAnswerList = (answerText) => {
    const whole = String(answerText || "");
    const parts = whole.split(/\u2192|\u21d2|->|=>/);
    return parts.length === 2 ? [whole, parts[1]] : [whole];
  };

  // 아이가 실제로 쳐야 하는 말. 고쳐 쓰기 문항이면 **고친 쪽**이다. 화살표는
  // 정답을 적어 둔 방식일 뿐이라 아이가 칠 이유가 없다. 힌트도 이것으로 연다.
  const targetAnswerText = (answerText) => {
    const list = acceptedAnswerList(answerText);
    return String(list[list.length - 1] || "").trim();
  };

  // **고쳐 쓰기는 칸을 둘로 받는다.** 틀린 곳을 찾는 것과 고치는 것이 각각
  // 문제가 시키는 일이라, 한 칸에 "A → B" 를 통째로 치게 하면 아이가 화살표를
  // 어떻게 넣어야 하는지부터 헤맨다(2026-09-04 지인학원).
  //
  // 정답이 **한 쌍**일 때만 두 칸으로 간다. "(A) for → of / (B) of → for" 처럼
  // 여러 쌍이거나 "또는" 이 붙은 답은 칸을 어떻게 나눌지가 정해지지 않으므로
  // 예전처럼 한 칸으로 받는다.
  const fixPairOf = (answerText) => {
    const whole = String(answerText || "").trim();
    if (!whole || /\/|또는|정답:/.test(whole)) return null;
    const parts = whole.split(/\u2192|\u21d2|->|=>/);
    if (parts.length !== 2) return null;
    const wrong = parts[0].trim();
    const right = parts[1].trim();
    return wrong && right ? { wrong, right } : null;
  };

  const finishInputPage = (patch) => {
    const newPageList = pageList.map((page, index) =>
      index === currentIndex ? { ...page, ...patch, isSubmitted: true } : page
    );
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setOverlayVisible(true);
    setTimeout(() => setOverlayVisible(false), 600);
  };

  const submitShort = () => {
    const submitText = arrangeWordList ? arrangeText : shortInput;
    if (!submitText.trim() || currentPage.isSubmitted) return;
    const isCorrect = acceptedAnswerList(currentPage.answer_text).some(
      (accepted) => normalizeAnswer(submitText) === normalizeAnswer(accepted)
    );
    if (isCorrect) {
      finishInputPage({
        input_text: submitText, isCorrect: true, hint_steps: hintSteps,
        arrange_picks: arrangePicks,
      });
      return;
    }
    const answer = targetAnswerText(currentPage.answer_text);
    // 칩 배열은 글자 힌트가 아니라 통째로 두 번 틀리면 정답을 보여 준다.
    // 글자 하나씩 열면 칩과 어긋나 오히려 헷갈린다.
    const step = (hintSteps.short || 0) + (arrangeWordList ? Math.ceil(answer.length / 2) : 1);
    if (step >= answer.length) {
      finishInputPage({
        input_text: submitText, isCorrect: false, hint_steps: { short: step },
        arrange_picks: arrangePicks,
      });
      return;
    }
    // 아직 열어 줄 글자가 남았다. 채점하지 않고 한 글자 더 보여 준 뒤 다시 받는다.
    // 칸을 비워야 그 자리에 힌트가 흐리게 뜬다.
    if (!arrangeWordList) setShortInput("");
    setHintSteps({ ...hintSteps, short: step });
  };

  const submitFix = () => {
    if (currentPage.isSubmitted) return;
    const pair = fixPairOf(currentPage.answer_text);
    if (!pair) return;
    const wrongInput = fixInputs.wrong.trim();
    const rightInput = fixInputs.right.trim();
    if (!wrongInput || !rightInput) return;

    const submitText = `${wrongInput} \u2192 ${rightInput}`;
    const isCorrect =
      normalizeAnswer(wrongInput) === normalizeAnswer(pair.wrong) &&
      normalizeAnswer(rightInput) === normalizeAnswer(pair.right);
    if (isCorrect) {
      finishInputPage({ input_text: submitText, isCorrect: true, hint_steps: hintSteps });
      return;
    }

    // 틀린 칸만 한 글자씩 더 열어 준다. 더 열 것이 없으면 그때 채점한다.
    const nextSteps = { ...hintSteps };
    let hasRoom = false;
    [["fix_wrong", wrongInput, pair.wrong], ["fix_right", rightInput, pair.right]].forEach(
      ([key, typed, answer]) => {
        if (normalizeAnswer(typed) === normalizeAnswer(answer)) return;
        const step = (nextSteps[key] || 0) + 1;
        nextSteps[key] = step;
        if (step < answer.length) hasRoom = true;
      }
    );
    if (!hasRoom) {
      finishInputPage({ input_text: submitText, isCorrect: false, hint_steps: nextSteps });
      return;
    }
    // 틀린 칸만 비운다. 맞게 쓴 쪽은 그대로 두어야 다시 칠 일이 없다.
    setFixInputs({
      wrong: normalizeAnswer(wrongInput) === normalizeAnswer(pair.wrong) ? wrongInput : "",
      right: normalizeAnswer(rightInput) === normalizeAnswer(pair.right) ? rightInput : "",
    });
    setHintSteps(nextSteps);
  };

  const submitCloze = () => {
    if (currentPage.isSubmitted) return;
    const blankList = currentPage.blanks || [];
    if (blankList.some((blank) => !String(clozeInputs[blank.blank_id] || "").trim())) return;
    const blankResults = {};
    let allCorrect = true;
    blankList.forEach((blank) => {
      const accepted = blank.accepted_answers || [blank.answer];
      const correct = accepted.some(
        (answer) => normalizeAnswer(answer) === normalizeAnswer(clozeInputs[blank.blank_id])
      );
      blankResults[blank.blank_id] = correct;
      if (!correct) allCorrect = false;
    });
    if (allCorrect) {
      finishInputPage({
        blank_inputs: clozeInputs, blank_results: blankResults,
        isCorrect: true, hint_steps: hintSteps,
      });
      return;
    }
    // 틀린 칸만 한 글자씩 더 열어 준다. 더 열 것이 없으면 그때 채점한다.
    const nextSteps = { ...hintSteps };
    let hasRoom = false;
    blankList.forEach((blank) => {
      if (blankResults[blank.blank_id]) return;
      const answer = String(blank.answer || (blank.accepted_answers || [])[0] || "");
      const step = (nextSteps[blank.blank_id] || 0) + 1;
      nextSteps[blank.blank_id] = step;
      if (step < answer.length) hasRoom = true;
    });
    if (!hasRoom) {
      finishInputPage({
        blank_inputs: clozeInputs, blank_results: blankResults,
        isCorrect: false, hint_steps: nextSteps,
      });
      return;
    }
    // 틀린 칸은 비운다. 그래야 그 자리에 힌트가 흐리게 뜬다. 맞힌 칸은 그대로 둔다.
    const clearedInputs = { ...clozeInputs };
    blankList.forEach((blank) => {
      if (!blankResults[blank.blank_id]) clearedInputs[blank.blank_id] = "";
    });
    setClozeInputs(clearedInputs);
    setHintSteps(nextSteps);
  };

  const normalizeDictation = (word) =>
    String(word || "").toLowerCase().replace(/[^a-z0-9']/g, "");

  const submitDictation = () => {
    if (currentPage.isSubmitted) return;
    const lines = currentPage.dictation_lines || [];
    let total = 0;
    let right = 0;
    const results = {};
    lines.forEach((line, lineIndex) => {
      (line.blank_indexes || []).forEach((tokenIndex) => {
        total += 1;
        const key = `${lineIndex}-${tokenIndex}`;
        const correct =
          normalizeDictation(clozeInputs[key]) ===
          normalizeDictation(line.tokens[tokenIndex]);
        results[key] = correct;
        if (correct) right += 1;
      });
    });
    if (right === total) {
      finishInputPage({
        blank_inputs: clozeInputs, blank_results: results,
        dictation_right: right, dictation_total: total,
        isCorrect: true, hint_steps: hintSteps,
      });
      return;
    }
    const nextSteps = { ...hintSteps };
    let hasRoom = false;
    lines.forEach((line, lineIndex) => {
      (line.blank_indexes || []).forEach((tokenIndex) => {
        const key = `${lineIndex}-${tokenIndex}`;
        if (results[key]) return;
        const answer = String(line.tokens[tokenIndex] || "");
        const step = (nextSteps[key] || 0) + 1;
        nextSteps[key] = step;
        if (step < answer.length) hasRoom = true;
      });
    });
    if (!hasRoom) {
      finishInputPage({
        blank_inputs: clozeInputs, blank_results: results,
        dictation_right: right, dictation_total: total,
        isCorrect: false, hint_steps: nextSteps,
      });
      return;
    }
    // 틀린 칸은 비운다. 그래야 그 자리에 힌트가 흐리게 뜬다. 맞힌 칸은 그대로 둔다.
    const clearedInputs = { ...clozeInputs };
    Object.keys(results).forEach((key) => {
      if (!results[key]) clearedInputs[key] = "";
    });
    setClozeInputs(clearedInputs);
    setHintSteps(nextSteps);
  };

  // [어법 복수 괄호] 발문 표시. **지금 풀고 있는 괄호만 볼드**로 도드라지게 한다.
  // 아직 차례가 아닌 괄호는 일반 굵기의 [a/b] 로 두고, 이미 푼 괄호는 정답
  // 낱말로 채워(일반 굵기) 문장이 점점 완성되게 한다.
  const blanksQuestion =
    (currentPage.blanks || []).length > 0
      ? (() => {
          const currentBlankIndex = currentPage.blanks.findIndex(
            (blank, index) => blankPicks[index] === undefined
          );
          let markerIndex = -1;
          return String(currentPage.question || "").replace(
            /\*\*\[(.*?)\]\*\*/g,
            (whole, inner) => {
              markerIndex += 1;
              const blank = currentPage.blanks[markerIndex];
              if (blankPicks[markerIndex] !== undefined && blank) {
                return blank.choices[blank.answer - 1];
              }
              const bracket = `[${inner.split(" / ").join("/")}]`;
              return markerIndex === currentBlankIndex
                ? `<b>${bracket}</b>`
                : bracket;
            }
          );
        })()
      : null;

  const selectBlankOption = ({ blankIndex, optionIndex }) => {
    if (currentPage.isSubmitted) return;
    if (blankPicks[blankIndex] !== undefined) return; // 그 빈칸은 이미 채점됐다
    const blanks = currentPage.blanks || [];
    const picks = { ...blankPicks, [blankIndex]: optionIndex + 1 };
    setBlankPicks(picks);
    // 고르는 순간 그 빈칸만 먼저 O/X 를 보여 준다. 다음 빈칸은 그 뒤에 나타난다.
    const blankCorrect = optionIndex + 1 === blanks[blankIndex].answer;
    setOverlayCorrect(blankCorrect);
    setOverlayVisible(true);
    setTimeout(() => setOverlayVisible(false), 600);
    if (Object.keys(picks).length < blanks.length) return;
    // 마지막 빈칸까지 골랐다. 하나라도 틀리면 문항 오답이고, 해설이 열린다.
    const isCorrect = blanks.every((blank, index) => picks[index] === blank.answer);
    const newPageList = pageList.map((page, index) =>
      index === currentIndex
        ? {
            ...page,
            isCorrect,
            input: blanks.map((_, index2) => picks[index2]),
            blank_picks: picks,
            isSubmitted: true,
          }
        : page
    );
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const selectOption = ({ optionIndex }) => {
    const realIndex = optionIndex + 1;
    const isCorrect = realIndex === currentPage.answer;
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isCorrect,
          input: realIndex,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setOverlayVisible(true);
    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  return (
    <>
      {/* 칸 안 힌트는 흐리게. inline style 로는 ::placeholder 를 잡을 수 없다. */}
      <style>{`
        .bank-hint-input::placeholder { color: #B9C1C9; letter-spacing: 0.06em; }
        .bank-hint-input::-webkit-input-placeholder { color: #B9C1C9; letter-spacing: 0.06em; }
      `}</style>
    <CommonPage
      currentIndex={currentIndex}
      currentPage={currentPage}
      handlePreviousPage={handlePreviousPage}
      handleNextPage={handleNextPage}
      totalNo={pageList.length}
      overlayVisible={overlayVisible}
      isCorrect={overlayCorrect === null ? currentPage.isCorrect : overlayCorrect}
    >
      {/* [문해력 UI 통일] 지문이 있는 문항은 데스크톱에서 모의고사 유형처럼
          왼쪽 '전체 본문' 카드 + 오른쪽 문항의 두 칸으로 놓는다. 폰은 세로 그대로. */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: "28px",
          width: "100%",
        }}
      >
      {currentPage.passage && !isMobileW && (
        <div
          style={{
            // 모의고사 유형(CommonPage)의 전체 본문 패널과 같은 규격
            flex: "0 0 40%",
            maxWidth: "560px",
            display: "flex",
            flexDirection: "column",
            maxHeight: "70vh",
            backgroundColor: "#FAFCFC",
            border: "1px solid #E6EDEC",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "13px 20px 11px",
              borderBottom: "1px solid #E6EDEC",
              fontSize: "12px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: accent,
              backgroundColor: "#FFFFFF",
            }}
          >
            전체 본문
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "18px 22px",
              fontSize: "16px",
              lineHeight: 1.9,
              color: "#0E141B",
              letterSpacing: "-0.01em",
              whiteSpace: "pre-wrap",
              wordBreak: "keep-all",
              textAlign: "left",
            }}
          >
            {currentPage.passage}
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // [여백 수술] 폰은 화면 기준 폭 — 조상 요소가 좁혀도 면역
          width: isMobileW ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "100%",
          maxWidth: "640px", // [어법 개선안] 전폭 75vw → 중앙 640px 열
          margin: "0 auto",
          // [여백] 데스크톱에서 문항이 길면 위 첫 줄과 아래 해설이 화면 가장자리에
          // 딱 붙어 답답했다. 스크롤 영역 안쪽에 위아래 숨 쉴 자리를 준다.
          padding: isMobileW ? "8px 8px 24px" : "24px 0 36px",
          // [잘림 수술] width:100% + 좌우 padding 조합에 boxSizing이 빠져
          // 실폭이 100%+16px가 되어 우측이 잘렸다(카운터 "3/40"→"3/4"처럼).
          boxSizing: "border-box",
          gap: isMobileW ? "18px" : "26px",
        }}
      >
        {/* [어법 개선안] 상단: Q칩 + 카운터 + 진행바 (기존엔 문항 번호가 문장 앞
            "1." 로 섞여 있었고 진행 표시는 최하단 고정바뿐이었음) */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "6px",
            }}
          >
            <span
              style={{
                backgroundColor: "#0E141B",
                color: "#FFF",
                borderRadius: "8px",
                fontSize: isMobileW ? "12px" : "13px",
                fontWeight: 800,
                padding: "2px 10px",
              }}
            >
              Q{currentIndex + 1}
            </span>
            {/* [문제뱅크] 문법 포인트 태그. 아이가 이 문항이 어느 포인트인지 알고
                풀어야 이해가 남는다 (PDF 의 출처 태그와 같은 역할). */}
            {currentPage.category && (
              <span
                style={{
                  fontSize: isMobileW ? "11px" : "12px",
                  fontWeight: 700,
                  color: accent,
                  background: "#F5F2FE",
                  border: `1px solid ${accent}33`,
                  borderRadius: "999px",
                  padding: "2px 10px",
                  maxWidth: "60%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {currentPage.category}
              </span>
            )}
            <span
              style={{
                marginLeft: "auto",
                fontSize: isMobileW ? "12px" : "13px",
                color: "#7A8490",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div
            style={{
              height: "4px",
              borderRadius: "999px",
              backgroundColor: "#EDF2F1",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`,
                backgroundColor: accent,
                borderRadius: "999px",
                transition: "width .25s",
              }}
            />
          </div>
        </div>
        {/* [문제뱅크 문해력] 폰에서는 발문 위에 지문을 둔다. 길어질 수 있으므로
            이 상자 안에서만 스크롤하게 해 선지가 화면 밖으로 밀리지 않게 한다.
            데스크톱은 위의 왼쪽 '전체 본문' 카드가 대신한다. */}
        {currentPage.passage && isMobileW && (
          <div
            style={{
              maxHeight: isMobileW ? "38vh" : "44vh",
              overflowY: "auto",
              background: "#F8FAFA",
              border: "1px solid #E6EDEC",
              borderRadius: "12px",
              padding: isMobileW ? "14px" : "18px",
              fontSize: isMobileW ? "15px" : "17px",
              lineHeight: 1.9,
              color: "#0E141B",
              whiteSpace: "pre-wrap",
              wordBreak: "keep-all",
              textAlign: "left",
            }}
          >
            {currentPage.passage}
          </div>
        )}
        {currentPage.audio && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <button
              type="button"
              onClick={playQuestionAudio}
              disabled={audioPlayedCount >= audioPlayLimit}
              style={{
                border: `1.5px solid ${audioPlayedCount >= audioPlayLimit ? "#E6EDEC" : accent}`,
                background: audioPlayedCount >= audioPlayLimit ? "#F4F8F7" : "#FFF",
                color: audioPlayedCount >= audioPlayLimit ? "#8A949C" : accent,
                borderRadius: "999px",
                padding: "10px 22px",
                fontSize: "15px",
                fontWeight: 800,
                fontFamily: "inherit",
                cursor: audioPlayedCount >= audioPlayLimit ? "default" : "pointer",
              }}
            >
              {audioPlayedCount === 0 ? "▶ 듣기" : audioPlayedCount < audioPlayLimit ? "▶ 다시 듣기" : "재생을 다 썼어요"}
            </button>
            <span style={{ fontSize: "12px", color: "#8A949C" }}>
              {audioPlayLimit === Infinity
                ? "몇 번이든 다시 들을 수 있어요"
                : `남은 재생 ${Math.max(0, audioPlayLimit - audioPlayedCount)}번`}
            </span>
          </div>
        )}
        <div
          style={{
            // [폰트 깔맞춤] 문법·듣기·문해력 전부 모의고사 유형과 같은 톤:
            // 발문 19px(폰 16)/600/왼쪽 정렬. 지문보다 발문이 커 보이던 것 정리.
            fontSize: isMobileW ? "16px" : "19px",
            lineHeight: 1.75,
            letterSpacing: "-0.01em",
            textAlign: "left",
            wordBreak: "keep-all",
          }}
        >
          {/* [문제뱅크 문법] 지시문(첫 줄)만 굵게, 문제 문장은 보통 굵기로 줄을
              바꿔 놓는다. 그래야 문장 속 [We/Us] 볼드가 도드라진다.
              한 줄짜리 발문(단어·문해력·듣기)은 기존 굵기(600) 그대로. */}
          {(() => {
            const lineList = (blanksQuestion ?? currentPage.question)
              .replace(
                "적고, 틀린 표현들을 모두 올바른 표현으로 고치시오.",
                "고르세요."
              )
              .split("\n")
              .filter((line) => line.trim())
              // 배열형은 단어 줄을 아래에서 칩으로 그리므로 여기서는 뺀다
              .filter((line) => !arrangeWordList || line !== arrangeWordList.slashedLine);
            return lineList.map((line, lineIndex) => (
              <div
                key={lineIndex}
                style={{
                  // 복수 괄호 문항은 문장을 보통 굵기로 두어야 지금 풀고 있는
                  // 괄호의 볼드가 도드라진다.
                  fontWeight: (currentPage.blanks || []).length
                    ? 400
                    : lineList.length === 1 ? 600 : lineIndex === 0 ? 700 : 400,
                  marginTop: lineIndex > 0 ? "6px" : 0,
                }}
              >
                {applyUnderline(line)}
              </div>
            ));
          })()}
        </div>
        {/* [문제뱅크 주관식] 우리말·조건·보기는 PDF 처럼 상자로 나눠 그린다 */}
        {currentPage.type === "bank_short" && currentPage.kr && (
          <div
            style={{
              background: "#F8FAFA",
              border: "1px solid #E6EDEC",
              borderRadius: "12px",
              padding: isMobileW ? "13px 14px" : "15px 18px",
              fontSize: isMobileW ? "15px" : "16px",
              lineHeight: 1.85,
              color: "#0E141B",
              wordBreak: "keep-all",
            }}
          >
            {currentPage.kr}
          </div>
        )}
        {currentPage.type === "bank_short" &&
          (currentPage.condition_list?.length > 0 || currentPage.word_bank?.length > 0) && (
          <div
            style={{
              border: `1.5px solid ${accent}44`,
              background: "#FBFAFF",
              borderRadius: "12px",
              padding: isMobileW ? "12px 14px" : "14px 18px",
            }}
          >
            <div
              style={{
                fontSize: "12.5px", fontWeight: 800, letterSpacing: "0.06em",
                color: accent, marginBottom: "6px",
              }}
            >
              조건
            </div>
            {(currentPage.condition_list || []).map((line, lineIndex) => (
              <div
                key={lineIndex}
                style={{ fontSize: isMobileW ? "14px" : "15px", lineHeight: 1.9, color: "#3A434E" }}
              >
                · {line}
              </div>
            ))}
            {currentPage.word_bank?.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                {currentPage.word_bank.map((word, wordIndex) => (
                  <span
                    key={wordIndex}
                    style={{
                      fontSize: "13.5px", padding: "3px 12px",
                      background: "#FFF", border: "1px solid #E6EDEC",
                      borderRadius: "999px", color: "#0E141B",
                    }}
                  >
                    {word}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {/* [문제뱅크 배열형] 단어 칩을 눌러 문장을 조립한다. 누른 칩은
            흐려지고, 조립 줄의 단어를 누르면 되돌아간다. 미끼 단어가 있어
            칩을 다 쓰지 않아도 제출할 수 있다. */}
        {currentPage.type === "bank_short" && arrangeWordList && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {arrangeWordList.words.map((word, wordIndex) => {
                const isPicked = arrangePicks.includes(wordIndex);
                return (
                  <button
                    key={wordIndex}
                    type="button"
                    disabled={currentPage.isSubmitted || isPicked}
                    onClick={() => setArrangePicks([...arrangePicks, wordIndex])}
                    style={{
                      padding: isMobileW ? "9px 13px" : "10px 16px",
                      border: "1.5px solid #E6EDEC", borderRadius: "999px",
                      background: isPicked ? "#F1F4F4" : "#fff",
                      color: isPicked ? "#C4CBD1" : "#0E141B",
                      fontSize: isMobileW ? "14.5px" : "15.5px", fontWeight: 600,
                      cursor: currentPage.isSubmitted || isPicked ? "default" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {word}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
              <div
                style={{
                  flex: 1, minWidth: 0, minHeight: "50px", padding: "10px 12px",
                  border: "1.5px solid #E6EDEC", borderRadius: "12px",
                  display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center",
                  background: currentPage.isSubmitted
                    ? currentPage.isCorrect ? "#E5FBF8" : "#FCEDE9"
                    : "#fff",
                }}
              >
                {arrangePicks.length === 0 && (
                  <span style={{ color: "#C4CBD1", fontSize: "15px" }}>
                    단어를 눌러 문장을 만드세요
                  </span>
                )}
                {arrangePicks.map((pickIndex, order) => (
                  <button
                    key={order}
                    type="button"
                    disabled={currentPage.isSubmitted}
                    onClick={() =>
                      setArrangePicks(arrangePicks.filter((_, at) => at !== order))
                    }
                    style={{
                      padding: "4px 10px", border: "none", borderRadius: "8px",
                      background: "#F1F4F4", color: "#0E141B",
                      fontSize: isMobileW ? "14.5px" : "15.5px", fontWeight: 600,
                      cursor: currentPage.isSubmitted ? "default" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {arrangeWordList.words[pickIndex]}
                  </button>
                ))}
              </div>
              {!currentPage.isSubmitted && (
                <button
                  type="button"
                  onClick={submitShort}
                  style={{
                    flex: "none", padding: "0 22px", border: "none",
                    borderRadius: "12px", background: accent, color: "#fff",
                    fontWeight: 800, fontSize: "15px", cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  제출
                </button>
              )}
            </div>
            {!currentPage.isSubmitted && hintSteps.short > 0 && (
              <div style={{ fontSize: "13px", color: "#C2452D" }}>
                칸에 뜬 글자를 보고 다시 써 보세요
              </div>
            )}
            {currentPage.isSubmitted && (
              <div style={{ fontSize: "15px", color: "#0E141B" }}>
                정답: <strong>{currentPage.answer_text}</strong>
              </div>
            )}
          </div>
        )}
        {/* [문제뱅크 주관식] 고쳐 쓰기: 틀린 곳과 고친 말을 따로 받는다.
            **틀린 곳을 찾는 것도 문제가 시키는 일이다.** 한 칸에 "A → B" 를
            통째로 치게 하면 화살표를 어떻게 넣는지부터 헤맨다. */}
        {currentPage.type === "bank_short" &&
          !arrangeWordList &&
          fixPairOf(currentPage.answer_text) && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="text"
                  className="bank-hint-input"
                  value={fixInputs.wrong}
                  disabled={currentPage.isSubmitted}
                  placeholder={
                    hintSteps.fix_wrong > 0
                      ? hintPlaceholder(fixPairOf(currentPage.answer_text).wrong, hintSteps.fix_wrong)
                      : "틀린 부분"
                  }
                  onChange={(event) =>
                    setFixInputs({ ...fixInputs, wrong: event.target.value })
                  }
                  onKeyDown={(event) => event.key === "Enter" && submitFix()}
                  style={{
                    flex: "1 1 140px", minWidth: "120px", padding: "12px 14px",
                    border: "1.5px solid #CBD5D3", borderRadius: "12px",
                    fontSize: "15px", fontFamily: "inherit",
                  }}
                />
                <span style={{ fontSize: "18px", color: "#8A949C", fontWeight: 800 }}>→</span>
                <input
                  type="text"
                  className="bank-hint-input"
                  value={fixInputs.right}
                  disabled={currentPage.isSubmitted}
                  placeholder={
                    hintSteps.fix_right > 0
                      ? hintPlaceholder(fixPairOf(currentPage.answer_text).right, hintSteps.fix_right)
                      : "바르게 고친 말"
                  }
                  onChange={(event) =>
                    setFixInputs({ ...fixInputs, right: event.target.value })
                  }
                  onKeyDown={(event) => event.key === "Enter" && submitFix()}
                  style={{
                    flex: "1 1 140px", minWidth: "120px", padding: "12px 14px",
                    border: "1.5px solid #CBD5D3", borderRadius: "12px",
                    fontSize: "15px", fontFamily: "inherit",
                  }}
                />
                {!currentPage.isSubmitted && (
                  <button
                    type="button"
                    onClick={submitFix}
                    style={{
                      flex: "none", padding: "12px 22px", border: "none",
                      borderRadius: "12px", background: accent, color: "#fff",
                      fontWeight: 800, fontSize: "15px", cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    제출
                  </button>
                )}
              </div>
              {!currentPage.isSubmitted &&
                (hintSteps.fix_wrong > 0 || hintSteps.fix_right > 0) && (
                  <div style={{ fontSize: "13px", color: "#C2452D" }}>
                    칸에 뜬 글자를 보고 다시 써 보세요
                  </div>
                )}
              {currentPage.isSubmitted && (
                <div style={{ fontSize: "15px", color: "#0E141B" }}>
                  정답: <strong>{currentPage.answer_text}</strong>
                </div>
              )}
            </div>
          )}
        {/* [문제뱅크 주관식] 서답형: 문장 입력 한 칸 */}
        {currentPage.type === "bank_short" &&
          !arrangeWordList &&
          !fixPairOf(currentPage.answer_text) && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                value={shortInput}
                className="bank-hint-input"
                disabled={currentPage.isSubmitted}
                placeholder={
                  hintSteps.short > 0
                    ? hintPlaceholder(targetAnswerText(currentPage.answer_text), hintSteps.short)
                    : acceptedAnswerList(currentPage.answer_text).length > 1
                    ? "고친 말만 쓰세요"
                    : "답을 입력하세요"
                }
                onChange={(event) => setShortInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && submitShort()}
                style={{
                  flex: 1, minWidth: 0, padding: "13px 16px",
                  border: "1.5px solid #E6EDEC", borderRadius: "12px",
                  fontSize: "16px", fontFamily: "inherit",
                  background: currentPage.isSubmitted
                    ? currentPage.isCorrect ? "#E5FBF8" : "#FCEDE9"
                    : "#fff",
                }}
              />
              {!currentPage.isSubmitted && (
                <button
                  type="button"
                  onClick={submitShort}
                  style={{
                    flex: "none", padding: "0 22px", border: "none",
                    borderRadius: "12px", background: accent, color: "#fff",
                    fontWeight: 800, fontSize: "15px", cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  제출
                </button>
              )}
            </div>
            {!currentPage.isSubmitted && hintSteps.short > 0 && (
              <div style={{ fontSize: "13px", color: "#C2452D" }}>
                칸에 뜬 글자를 보고 다시 써 보세요
              </div>
            )}
            {currentPage.isSubmitted && (
              <div style={{ fontSize: "15px", color: "#0E141B" }}>
                정답: <strong>{currentPage.answer_text}</strong>
              </div>
            )}
          </div>
        )}
        {/* [문제뱅크 듣기 2차] 받아쓰기: 대사 속 빈칸(한 줄 최대 2단어)을 채운다 */}
        {currentPage.type === "bank_dictation" && (
          <div
            style={{
              display: "flex", flexDirection: "column", gap: "10px",
              background: "#F8FAFA", border: "1px solid #E6EDEC",
              borderRadius: "14px", padding: isMobileW ? "14px" : "18px 20px",
            }}
          >
            {(currentPage.dictation_lines || []).map((line, lineIndex) => (
              <div key={lineIndex} style={{ fontSize: isMobileW ? "15px" : "16px", lineHeight: 2.2, wordBreak: "keep-all" }}>
                <strong
                  style={{
                    marginRight: "6px",
                    color: String(line.speaker).startsWith("NARR")
                      ? "#8A949C"
                      : String(line.speaker).startsWith("W") ? "#C2452D" : "#3E6398",
                  }}
                >
                  {String(line.speaker).startsWith("NARR")
                    ? "안내"
                    : String(line.speaker).startsWith("W") ? "여" : "남"} :
                </strong>
                {line.tokens.map((token, tokenIndex) => {
                  const key = `${lineIndex}-${tokenIndex}`;
                  if (!(line.blank_indexes || []).includes(tokenIndex)) {
                    return <span key={tokenIndex}>{token} </span>;
                  }
                  const correct = currentPage.blank_results?.[key];
                  return (
                    <span key={tokenIndex}>
                      <input
                        type="text"
                        value={
                          currentPage.isSubmitted && !correct
                            ? `${clozeInputs[key] || ""} → ${token}`
                            : clozeInputs[key] || ""
                        }
                        disabled={currentPage.isSubmitted}
                        autoCapitalize="off"
                        autoComplete="off"
                        spellCheck={false}
                        className="bank-hint-input"
                        placeholder={
                          hintSteps[key] > 0
                            ? hintPlaceholder(token, hintSteps[key])
                            : "들리는 단어"
                        }
                        onChange={(event) =>
                          setClozeInputs({ ...clozeInputs, [key]: event.target.value })
                        }
                        style={{
                          width: currentPage.isSubmitted && !correct ? "150px" : "104px",
                          margin: "0 4px", padding: "5px 9px",
                          border: "1.5px solid",
                          borderColor: currentPage.isSubmitted
                            ? correct ? "#00D9C0" : "#C2452D"
                            : "#CBD5D3",
                          borderRadius: "9px", fontSize: "14px",
                          fontFamily: "inherit", textAlign: "center",
                          background: currentPage.isSubmitted
                            ? correct ? "#E5FBF8" : "#FCEDE9"
                            : "#fff",
                        }}
                      />
                      {" "}
                    </span>
                  );
                })}
              </div>
            ))}
            {!currentPage.isSubmitted ? (
              <button
                type="button"
                onClick={submitDictation}
                style={{
                  alignSelf: "flex-start", padding: "10px 24px", border: "none",
                  borderRadius: "12px", background: accent, color: "#fff",
                  fontWeight: 800, fontSize: "15px", cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {Object.keys(hintSteps).length > 0 ? "다시 채점하기" : "채점하기"}
              </button>
            ) : (
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#0E141B" }}>
                빈칸 {currentPage.dictation_total}개 중 {currentPage.dictation_right}개 정답
              </div>
            )}
          </div>
        )}
        {/* [문제뱅크 주관식] 국어 빈칸: 문장 틀에 빈칸을 채운다 */}
        {currentPage.type === "bank_cloze" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ fontSize: "17px", lineHeight: 2.1, wordBreak: "keep-all" }}>
              {(currentPage.answer_parts || []).map((part, partIndex) =>
                part.type === "blank" ? (
                  <input
                    key={partIndex}
                    type="text"
                    className="bank-hint-input"
                    value={clozeInputs[part.blank_id] || ""}
                    disabled={currentPage.isSubmitted}
                    placeholder={hintPlaceholder(
                      (currentPage.blanks || []).find(
                        (blank) => blank.blank_id === part.blank_id
                      )?.answer ||
                        ((currentPage.blanks || []).find(
                          (blank) => blank.blank_id === part.blank_id
                        )?.accepted_answers || [])[0],
                      hintSteps[part.blank_id]
                    )}
                    onChange={(event) =>
                      setClozeInputs({ ...clozeInputs, [part.blank_id]: event.target.value })
                    }
                    style={{
                      width: "110px", margin: "0 4px", padding: "6px 10px",
                      border: "1.5px solid",
                      borderColor: currentPage.isSubmitted
                        ? currentPage.blank_results?.[part.blank_id] ? "#00D9C0" : "#C2452D"
                        : "#CBD5D3",
                      borderRadius: "9px", fontSize: "15px",
                      fontFamily: "inherit", textAlign: "center",
                      background: currentPage.isSubmitted
                        ? currentPage.blank_results?.[part.blank_id] ? "#E5FBF8" : "#FCEDE9"
                        : "#fff",
                    }}
                  />
                ) : (
                  <span key={partIndex}>{part.value}</span>
                )
              )}
              {!currentPage.isSubmitted &&
                (currentPage.blanks || []).some((blank) => hintSteps[blank.blank_id] > 0) && (
                  <div style={{ fontSize: "13px", color: "#C2452D", marginTop: "8px" }}>
                    칸에 뜬 글자를 보고 다시 써 보세요
                  </div>
                )}
            </div>
            {!currentPage.isSubmitted ? (
              <button
                type="button"
                onClick={submitCloze}
                style={{
                  alignSelf: "flex-start", padding: "11px 26px", border: "none",
                  borderRadius: "12px", background: accent, color: "#fff",
                  fontWeight: 800, fontSize: "15px", cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                제출
              </button>
            ) : (
              <div style={{ fontSize: "15px", color: "#0E141B" }}>
                모범답안: <strong>{currentPage.model_answer}</strong>
              </div>
            )}
          </div>
        )}
        {/* [어법 복수 괄호] 빈칸을 하나씩 차례로 낸다. 첫 빈칸을 고르면 그
            빈칸만 바로 O/X 로 갈리고, 그 아래에 다음 빈칸 선지가 나타난다.
            줄 모양·색은 단일 선택 선지와 완전히 같다.

            **선지가 있는 빈칸만 그린다.** 문제뱅크 국어 빈칸(bank_cloze)도 blanks 를
            들고 있는데 그 빈칸에는 선지가 없다. 거르지 않으면 blank.choices 가
            undefined 라 화면이 통째로 죽어서 아이가 그 문항을 풀 수 없었다
            (2026-09-02 라이트하우스 최호중. 이 유형은 한 번도 제출된 적이 없었다). */}
        {choiceBlankList.length > 0 &&
          choiceBlankList
            .slice(
              0,
              currentPage.isSubmitted
                ? choiceBlankList.length
                : Object.keys(blankPicks).length + 1
            )
            .map((blank, blankIndex) => {
              const pick = blankPicks[blankIndex];
              const graded = pick !== undefined;
              return (
                <div
                  key={blankIndex}
                  style={{ display: "flex", flexDirection: "column", gap: "14px" }}
                >
                  {blank.choices.map((option, optionIndex) => {
                    const isAnswer = blank.answer === optionIndex + 1;
                    const isPicked = pick === optionIndex + 1;
                    return (
                      <div
                        key={optionIndex}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          textAlign: "left",
                          gap: "8px",
                          padding: isMobileW ? "12px 13px" : "14px 18px",
                          border:
                            graded && isAnswer
                              ? "1.5px solid #00D9C0"
                              : graded && isPicked
                              ? "1.5px solid #C2452D"
                              : "1px solid #E6EDEC",
                          borderRadius: "12px",
                          fontWeight: graded && (isAnswer || isPicked) ? 700 : 500,
                          cursor: !graded && "pointer",
                          backgroundColor:
                            graded && isAnswer
                              ? "#E5FBF8"
                              : graded && isPicked
                              ? "#FCEDE9"
                              : "#fff",
                          color:
                            graded && isAnswer
                              ? "#00A396"
                              : graded && isPicked
                              ? "#C2452D"
                              : "#3A434E",
                          fontSize: isMobileW ? "15px" : "16.5px",
                          letterSpacing: "-0.01em",
                          lineHeight: 1.65,
                          wordBreak: "keep-all",
                          transition: "background-color .12s, border-color .12s",
                        }}
                        onClick={() => selectBlankOption({ blankIndex, optionIndex })}
                      >
                        {graded && isAnswer && "✓ "}
                        {option}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {(currentPage.choices || []).map((option, optionIndex) => {
            return (
              <div
                key={optionIndex}
                style={{
                  display: "flex",
                  alignItems: "center",
                  // [폰트 깔맞춤] 모의고사 유형처럼 왼쪽 정렬
                  justifyContent: "flex-start",
                  textAlign: "left",
                  gap: "8px",
                  padding: isMobileW ? "12px 13px" : "14px 18px",
                  border:
                    currentPage.isSubmitted &&
                    currentPage.answer === optionIndex + 1
                      ? "1.5px solid #00D9C0"
                      : currentPage.isSubmitted &&
                        currentPage.input === optionIndex + 1
                      ? "1.5px solid #C2452D"
                      : "1px solid #E6EDEC",
                  borderRadius: "12px",
                  fontWeight:
                    currentPage.isSubmitted &&
                    (optionIndex + 1 === currentPage.answer ||
                      optionIndex + 1 === currentPage.input)
                      ? 700
                      : 500,
                  cursor: !currentPage.isSubmitted && "pointer",
                  backgroundColor:
                    currentPage.isSubmitted &&
                    currentPage.answer === optionIndex + 1
                      ? "#E5FBF8"
                      : currentPage.isSubmitted &&
                        currentPage.input === optionIndex + 1
                      ? "#FCEDE9"
                      : "#fff",
                  color:
                    currentPage.isSubmitted &&
                    currentPage.answer === optionIndex + 1
                      ? "#00A396"
                      : currentPage.isSubmitted &&
                        currentPage.input === optionIndex + 1
                      ? "#C2452D"
                      : "#3A434E",
                  fontSize: isMobileW ? "15px" : "16.5px", // [폰트 깔맞춤] 모의고사와 동일
                  letterSpacing: "-0.01em",
                  lineHeight: 1.65,
                  wordBreak: "keep-all",
                  transition: "background-color .12s, border-color .12s",
                }}
                onClick={() => {
                  if (currentPage.isSubmitted) return;
                  selectOption({ optionIndex });
                }}
              >
                {currentPage.isSubmitted &&
                  currentPage.answer === optionIndex + 1 &&
                  "✓ "}
                {applyUnderline(option)}
              </div>
            );
          })}
        </div>
        {/* 지인에듀 어법의 한글 해석(kor). 지금 문제지들은 비어 오지만, 채워
            보내는 문제지가 생기면 채점 뒤에 해설 위로 뜬다. 미리 보여 주면
            힌트가 되므로 채점 후에만 연다. */}
        {currentPage.isSubmitted &&
          currentPage.type === "grammar:sentence" &&
          String(currentPage.kor || "").trim() && (
            <div
              style={{
                background: "#F8FAFA",
                border: "1px solid #E6EDEC",
                borderRadius: "12px",
                padding: isMobileW ? "13px 14px" : "15px 18px",
                fontSize: isMobileW ? "14px" : "15px",
                lineHeight: 1.8,
                color: "#3A434E",
                wordBreak: "keep-all",
              }}
            >
              <strong style={{ marginRight: "8px", color: "#0E141B" }}>해석</strong>
              {currentPage.kor}
            </div>
          )}
        {currentPage.isSubmitted && currentPage.explanation && (
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: typeInfo["grammar"].backgroundColor, // [색 동기화]
              borderRadius: "0 14px 14px 0",
              borderLeft: `3px solid ${typeInfo["grammar"].color}`, // [색 동기화]
            }}
          >
            <div
              onClick={() => setExplOpen(!explOpen)}
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 800,
                marginBottom: explOpen ? "8px" : 0,
                fontSize: "13px",
                letterSpacing: "0.05em",
                color: typeInfo["grammar"].color, // [색 동기화]
                cursor: "pointer",
              }}
            >
              해설
              <span style={{ marginLeft: "auto", fontSize: "11px", color: "#7A8490" }}>
                {explOpen ? "접기 ▲" : "펼치기 ▼"}
              </span>
            </div>
            {explOpen && (
            <div
              style={{
                fontSize: isMobileW ? "14px" : "16px",
                lineHeight: "1.7",
                color: "#3A434E",
                // [해설 스크롤] 해설이 길면 화면을 밀어내지 않고 박스 안에서 스크롤
                maxHeight: isMobileW ? "30vh" : "28vh",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                paddingRight: "6px",
              }}
            >
              <div style={{ whiteSpace: "pre-wrap" }}>{currentPage.explanation}</div>
              {/* [문제뱅크 듣기] 대사는 지문 상자와 같은 톤의 대화 상자로.
                  화자(W1/M1)는 굵게, 행간은 독해 지문과 맞춘다. */}
              {currentPage.script_lines?.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    background: "#F8FAFA",
                    border: "1px solid #E6EDEC",
                    borderRadius: "12px",
                    padding: isMobileW ? "12px 14px" : "14px 18px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      letterSpacing: "0.05em",
                      color: "#7A8490",
                      marginBottom: "8px",
                    }}
                  >
                    대사
                  </div>
                  {currentPage.script_lines.map((line, lineIndex) => (
                    <div
                      key={lineIndex}
                      style={{
                        fontSize: isMobileW ? "14px" : "15px",
                        lineHeight: 1.9,
                        color: "#0E141B",
                        wordBreak: "keep-all",
                      }}
                    >
                      {line.speaker && <strong style={{ marginRight: "6px" }}>{line.speaker}</strong>}
                      {line.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        )}
      </div>
      </div>
    </CommonPage>
    </>
  );
};

export default BasicChoicePage;
